<template>
  <div>
    <v-dialog v-model="dialog" max-width="1000px" scrollable persistent>
      <v-card style="border-radius: 12px;">
        <v-toolbar flat color="white" class="px-6" elevation="0">
          <div class="d-flex align-center">
            <div>
              <h2 class="text-h6 font-weight-bold grey--text text--darken-4">
                Imagens do Produto
              </h2>
            </div>
            <v-switch
              v-if="$acl.isManager() || $acl.isAdministrativeAssistant()"
              v-model="viewAsUser"
              label="Visualizar como usuário"
              dense
              hide-details
              class="ml-4 mt-0"
              :style="{ opacity: viewAsUser ? 0.1 : 1 }"
            ></v-switch>
          </div>

          <v-spacer />

          <v-btn
            icon
            @click="close"
            color="grey lighten-1"
            class="rounded-circle"
          >
            <v-icon color="grey darken-2" size="22">mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="px-6">
          <div class="py-4">
            <template v-if="loading">
              <v-row>
                <v-col cols="12" md="6">
                  <v-row>
                    <v-col v-for="n in 6" :key="n" cols="6" sm="4" md="4">
                      <v-skeleton-loader
                        type="image, list-item-two-line"
                        class="gallery-image"
                        style="border-radius: 8px;"
                      />
                    </v-col>
                  </v-row>
                </v-col>

                <v-col cols="12" md="6">
                  <v-card class="pa-4" elevation="0">
                    <v-skeleton-loader type="heading" class="mb-2" />
                    <v-skeleton-loader type="heading" class="mb-4" />

                    <v-divider class="my-3" />
                    <v-row dense>
                      <v-col cols="6">
                        <v-skeleton-loader type="text" />
                        <v-skeleton-loader type="heading" class="mt-2" />
                      </v-col>
                      <v-col cols="6">
                        <v-skeleton-loader type="text" />
                        <v-skeleton-loader type="heading" class="mt-2" />
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>
            </template>

            <v-row v-if="!loading && product">
              <v-col cols="12" md="6">
                <v-row v-if="product.product_images.length > 0">
                  <v-col
                    v-for="image in product.product_images"
                    :key="image.id"
                    cols="6"
                    sm="4"
                    md="4"
                  >
                    <v-card
                      :class="[
                        'image-card',
                        { selected: isImageSelected(image) },
                        { 'no-permission': !hasPermission },
                      ]"
                      @click="handleImageClick(image)"
                      elevation="0"
                      outlined
                    >
                      <v-img
                        :src="getImage(image)"
                        aspect-ratio="1"
                        contain
                        class="gallery-image"
                      >
                        <template v-slot:placeholder>
                          <v-row
                            class="fill-height ma-0"
                            align="center"
                            justify="center"
                          >
                            <v-progress-circular
                              indeterminate
                              color="grey lighten-2"
                            />
                          </v-row>
                        </template>
                      </v-img>

                      <div class="action-buttons-top" v-if="hasPermission">
                        <v-tooltip bottom v-if="!isPrimaryImage(image)">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              x-small
                              color="primary"
                              @click.stop="setPrimaryImage(image)"
                              class="action-btn"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon size="16">mdi-star</v-icon>
                            </v-btn>
                          </template>
                          <span>Definir como imagem principal</span>
                        </v-tooltip>
                      </div>

                      <v-chip
                        v-if="isPrimaryImage(image) && hasPermission"
                        x-small
                        color="primary"
                        class="primary-tag"
                        label
                      >
                        <v-icon left x-small>mdi-star</v-icon>
                        Principal
                      </v-chip>

                      <div
                        v-if="isImageSelected(image)"
                        class="selected-overlay"
                      >
                        <v-icon color="white" size="48">
                          mdi-close-circle
                        </v-icon>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>

                <div v-else class="text-center grey--text text--darken-1 pa-16">
                  <v-icon size="64" color="grey lighten-1">
                    mdi-image-off
                  </v-icon>
                  <p class="mt-4 mb-0 text-h6">Nenhuma imagem associada</p>
                  <p class="caption mt-2 mb-0">
                    Este produto ainda não possui imagens.
                  </p>
                </div>
              </v-col>

              <v-col md="6">
                <v-card class="pa-4" elevation="0">
                  <div class="d-flex flex-column">
                    <div class="mb-4">
                      <span
                        class="text-caption grey--text text--darken-1 mb-1 d-block"
                      >
                        Nome
                      </span>
                      <span class="font-weight-medium text-body-1">
                        {{ product.name }}
                      </span>

                      <p class=" text-body-6 mt-2">
                        {{ product.short_name || "-" }}
                      </p>
                    </div>

                    <v-divider class="my-3" />

                    <v-row dense>
                      <v-col cols="6">
                        <div class="d-flex flex-column">
                          <span
                            class="text-caption grey--text text--darken-1 mb-1"
                          >
                            Preço à Vista
                          </span>
                          <span class="font-weight-bold text-h6 ">
                            R$
                            {{
                              $format.decimal(
                                getProductVariantPrice("price_cash")
                              )
                            }}
                          </span>
                        </div>
                      </v-col>

                      <v-col cols="6">
                        <div class="d-flex flex-column">
                          <span
                            class="text-caption grey--text text--darken-1 mb-1"
                          >
                            Preço a Prazo
                          </span>
                          <span class="font-weight-bold text-h6 ">
                            R$
                            {{
                              $format.decimal(
                                getProductVariantPrice("price_forward")
                              )
                            }}
                          </span>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="px-6 py-4">
          <v-btn
            v-if="hasPermission"
            color="primary"
            outlined
            @click="openImageModal"
            :disabled="!product"
          >
            <v-icon left>mdi-image-plus</v-icon>
            Adicionar da Galeria
          </v-btn>
          <v-spacer />
          <v-btn text @click="close" color="grey darken-1">
            Fechar
          </v-btn>
          <v-btn
            v-if="selectedImages.length > 0 && hasPermission"
            color="error"
            @click="confirmDesassociating"
            :loading="desassociating"
            elevation="0"
          >
            Remover Selecionadas ({{ selectedImages.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ProductImageGalleryModal
      ref="imageGalleryModal"
      :product-ids="[product ? product.id : null]"
      @updated="handleGalleryUpdated"
    />

    <v-dialog v-model="imageViewDialog" max-width="900px">
      <v-card>
        <v-toolbar flat dense color="white">
          <v-spacer />
          <v-btn icon @click="imageViewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-0">
          <v-carousel
            v-model="currentImageIndex"
            height="70vh"
            hide-delimiters
            show-arrows-on-hover
          >
            <v-carousel-item
              v-for="(image, index) in product ? product.product_images : []"
              :key="index"
            >
              <v-img :src="getImage(image, 'large')" contain height="100%">
                <template v-slot:placeholder>
                  <v-row
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                  >
                    <v-progress-circular indeterminate color="grey lighten-2" />
                  </v-row>
                </template>
              </v-img>
            </v-carousel-item>
          </v-carousel>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <span class="text-caption grey--text">
            {{ currentImageIndex + 1 }} /
            {{ product ? product.product_images.length : 0 }}
          </span>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import MediaMixin from "@/mixins/Media/MediaMixin";
import ProductImageGalleryModal from "@/components/app/forms/ProductImageGalleryModal.vue";

export default {
  name: "ProductImagesForm",

  mixins: [MediaMixin],

  components: {
    ProductImageGalleryModal,
  },

  data() {
    return {
      loading: false,
      dialog: false,

      product: null,

      viewAsUser: false,

      selectedImages: [],
      desassociating: false,
      imageModalOpen: false,

      imageViewDialog: false,
      currentImageIndex: 0,
    };
  },

  computed: {
    hasPermission() {
      if (this.viewAsUser) {
        return false;
      }
      return this.$acl.isManager() || this.$acl.isAdministrativeAssistant();
    },
  },

  methods: {
    getProductVariantPrice(field) {
      if (
        !this.product ||
        !this.product.product_variants ||
        !this.product.product_variants[0]
      ) {
        return null;
      }
      return this.product.product_variants[0][field];
    },

    selectImage(image) {
      const index = this.selectedImages.findIndex((img) => img.id === image.id);

      if (index > -1) {
        this.selectedImages.splice(index, 1);
      } else {
        this.selectedImages.push(image);
      }
    },

    viewImage(image) {
      this.currentImageIndex = this.product.product_images.findIndex(
        (img) => img.id === image.id
      );
      this.imageViewDialog = true;
    },

    handleImageClick(image) {
      if (this.hasPermission) {
        this.selectImage(image);
      } else {
        this.viewImage(image);
      }
    },

    isImageSelected(image) {
      return this.selectedImages.some((img) => img.id === image.id);
    },

    isPrimaryImage(image) {
      return image.pivot && image.pivot.is_primary === 1;
    },

    async handleGalleryUpdated() {
      if (this.product && this.product.id) {
        await this.loadProductWithMedia(this.product.id);
      }
    },

    openImageModal() {
      this.$refs.imageGalleryModal.open();
    },

    async loadProductWithMedia(id) {
      this.loading = true;

      await this.$http
        .show("product/image-gallery", id)
        .then((response) => {
          this.product = response.product;
        })
        .catch((error) => {
          console.error("Erro ao carregar produto e mídia:", error);
        });

      this.loading = false;
    },

    async confirmDesassociating() {
      if (this.selectedImages.length === 0) return;

      this.desassociating = true;

      try {
        const imageIds = this.selectedImages.map((img) => img.id);

        await this.$http.store("product/mass-disassociate-images", {
          product_ids: [this.product.id],
          image_ids: imageIds,
        });

        this.selectedImages = [];

        await this.loadProductWithMedia(this.product.id);

        this.$emit("updated");
      } catch (error) {
        console.error("Erro ao desassociar imagens:", error);
      } finally {
        this.desassociating = false;
      }
    },

    async setPrimaryImage(image) {
      try {
        await this.$http.update("product/image-gallery", this.product.id, {
          media_id: image.id,
          is_primary: true,
        });

        await this.loadProductWithMedia(this.product.id);
      } catch (error) {
        console.error("Erro ao definir imagem principal:", error);
      }
    },

    open(id) {
      this.loadProductWithMedia(id);
      this.selectedImages = [];
      this.dialog = true;
    },

    close() {
      this.selectedImages = [];
      this.dialog = false;
    },
  },
};
</script>

<style scoped>
.image-card {
  cursor: pointer;
  outline: solid 1px transparent;
  position: relative;
  border-radius: 8px;
  transition: all 0.3s ease;
  border: 1px solid #00000014;
}

.image-card:hover {
  background-color: #fafafa;
  outline: solid 1px #999;
}

.image-card:hover .action-buttons-top {
  opacity: 1;
}

.gallery-image {
  background-color: #fafafa;
}

.action-buttons-top {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.action-btn {
  background-color: white !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.primary-tag {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 3;
}

.selected-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(244, 67, 54, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  outline: solid 2px #f44336;
}
</style>
